import { ApolloError } from "@apollo/client";
import {
  Box,
  CircularProgress,
  CircularProgressLabel,
  Divider,
  Flex,
  Heading,
  Icon,
  StyleProps,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import React, { useCallback } from "react";
import { HiOutlineMinusCircle, HiOutlinePlusCircle } from "react-icons/hi";

import {
  Alert,
  IconButton,
  LoadingIndicator,
  useToast,
} from "../../../../components";
import CopyOutlineIcon from "../../../../components/Icons/CopyOutlineIcon";
import { DeepPick } from "../../../../types";
import { copy } from "../../../../utils/clipboard";
import { formatDurationShort } from "../../../../utils/datetime";
import {
  AiInterviewEvaluationFragment,
  AiInterviewQuestionType,
} from "../../../graphql";
import useFeatureFlag from "../../../graphql/hooks/useFeatureFlag";
import DisqualifiedBadge from "../../AiInterviewer/DisqualifiedBadge";
import CandidateQuestionsSection from "./CandidateQuestionsSection";
import { Citations } from "./Citations";
import { EvaluationScorePopover } from "./EvaluationScorePopover";
import { RedactionAlert } from "./RedactionAlert";
import RegenerateEvaluationButton from "./RegenerateEvaluationButton";
import ScoreIcon from "./ScoreIcon";
import {
  calculateFinalScore,
  getFinalScoreColor,
  shouldShowScores,
} from "./scoring";
import { useEvaluationCitationMap } from "./useEvaluationCitationMap";

type InterviewEvaluation = Pick<
  AiInterviewEvaluationFragment,
  | "summary"
  | "points"
  | "maxPoints"
  | "disqualified"
  | "summaryCitations"
  | "candidateQuestions"
> &
  DeepPick<
    AiInterviewEvaluationFragment,
    "questions",
    | "id"
    | "summary"
    | "question"
    | "points"
    | "qualifies"
    | "summaryCitations"
    | "wasAnswerRedacted"
  > &
  DeepPick<
    AiInterviewEvaluationFragment,
    "keyPoints",
    "id" | "content" | "positive" | "citations"
  >;

type EvaluationProps = StyleProps & {
  aiInterviewEvaluation?: InterviewEvaluation | null;
  callId: string;
  callDuration?: number | null;
  loading: boolean;
  error?: ApolloError;
  onPlayMedia?: () => void;
  hasRedaction?: boolean;
};

const REDACTED_SCORING_QUESTION_NOTE =
  "Parts of the candidate's response were redacted, so the response was not scored and it was removed from the total score calculation.";
const REDACTED_NON_SCORING_QUESTION_NOTE =
  "Parts of the candidate's response were redacted, so a summary of the candidates response has not been generated";
const REDACTED_QUALIFICATION_QUESTION_NOTE =
  "Parts of the candidate's response were redacted, so it could not be evaluated for qualification or disqualification.";

const getQuestionType = (question: {
  points?: number | null;
  qualifies?: boolean | null;
}): AiInterviewQuestionType => {
  if (question.points) {
    return AiInterviewQuestionType.Score;
  }
  if (question.qualifies !== null && question.qualifies !== undefined) {
    return AiInterviewQuestionType.Qualification;
  }
  return AiInterviewQuestionType.Summary;
};

const getQuestionRedactionDescription = (question: {
  points?: number | null;
  qualifies?: boolean | null;
}): string => {
  const questionType = getQuestionType(question);
  if (questionType === AiInterviewQuestionType.Score) {
    return REDACTED_SCORING_QUESTION_NOTE;
  }
  if (questionType === AiInterviewQuestionType.Qualification) {
    return REDACTED_QUALIFICATION_QUESTION_NOTE;
  }
  return REDACTED_NON_SCORING_QUESTION_NOTE;
};

const Evaluation: React.FC<EvaluationProps> = ({
  aiInterviewEvaluation,
  callId,
  callDuration,
  loading,
  error,
  onPlayMedia,
  hasRedaction,
  ...styles
}) => {
  const showRegenerate = useFeatureFlag("ai_interview:regenerate_evaluation");
  const questions = aiInterviewEvaluation?.questions ?? [];
  const toast = useToast();

  const citationMap = useEvaluationCitationMap(aiInterviewEvaluation);

  const handleCopyEvaluation = useCallback(() => {
    if (!aiInterviewEvaluation) {
      return;
    }
    // Plain text version
    let plain = `Evaluation Summary:\n${aiInterviewEvaluation.summary}\n`;
    let html = `<strong>Evaluation Summary:</strong><br>${aiInterviewEvaluation.summary}<br>`;
    if (questions.length) {
      plain += "\nQuestions:";
      html += "<br><strong>Questions:</strong>";
      questions.forEach((q, idx: number) => {
        plain += `\n${idx + 1}. ${q.question?.question || ""}\n   ${q.summary}`;
        html += `<br><b>${idx + 1}. ${
          q.question?.question || ""
        }</b><br>&nbsp;&nbsp;${q.summary}`;
      });
    }
    const candidateQuestions = aiInterviewEvaluation.candidateQuestions ?? [];
    if (candidateQuestions.length) {
      plain += "\n\nCandidate Questions:";
      html += "<br><br><strong>Candidate Questions:</strong>";
      candidateQuestions.forEach((cq) => {
        const status = cq.answered ? "Answered" : "Unanswered";
        plain += `\n- "${cq.questionText}" (${status})`;
        html += `<br>&bull; &ldquo;${cq.questionText}&rdquo; (${status})`;
        if (cq.answerSummary) {
          plain += `\n  ${cq.answerSummary}`;
          html += `<br>&nbsp;&nbsp;${cq.answerSummary}`;
        }
      });
    }
    copy({ plain, html });
    toast({
      title: "Evaluation copied to clipboard",
      status: "success",
    });
  }, [aiInterviewEvaluation, questions, toast]);

  if (error) {
    return (
      <Alert
        w="100%"
        status="error"
        title="Error"
        description={error.message}
      />
    );
  }

  if (loading) {
    return <LoadingIndicator />;
  }

  if (!aiInterviewEvaluation) {
    return (
      <Flex
        w="100%"
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        {...styles}
      >
        <Text fontSize="sm" color="gray.500">
          No evaluation available.
        </Text>
        {showRegenerate && <RegenerateEvaluationButton callId={callId} />}
      </Flex>
    );
  }

  const showScores = shouldShowScores(aiInterviewEvaluation);
  const finalScore = calculateFinalScore(aiInterviewEvaluation);
  const finalScoreColor = getFinalScoreColor(finalScore);

  // Check if candidate is disqualified based on requirement questions
  const isDisqualified = aiInterviewEvaluation.disqualified;

  // Count how many qualifications were not met for the tooltip
  const unmetRequirementsCount =
    aiInterviewEvaluation.questions?.filter((q) => q.qualifies === false)
      .length || 0;

  return (
    <Flex direction="column" h="100%" minH="0" bg="white" {...styles}>
      {hasRedaction && (
        <RedactionAlert
          mb={6}
          description="One or more responses from this interview were redacted and may have impacted the candidate's score. See below for details."
        />
      )}
      <Flex
        w="100%"
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        flexShrink={0}
        mb={6}
      >
        <Flex align="center" gap="3">
          <Text fontSize="sm" fontWeight={500} color="gray.500">
            {`${questions.length} questions`}
            {callDuration ? ` | ${formatDurationShort(callDuration)}` : ""}
          </Text>
          {isDisqualified && (
            <DisqualifiedBadge
              unmetRequirementsCount={unmetRequirementsCount}
            />
          )}
        </Flex>
        <Flex gap={2}>
          {showRegenerate && <RegenerateEvaluationButton callId={callId} />}
          <Tooltip id="copyEvaluation" placement="top" label="Copy evaluation">
            <IconButton
              icon={<CopyOutlineIcon width="16px" height="16px" />}
              aria-label="Copy evaluation"
              size="xs"
              p="2"
              variant="ghost"
              height="8"
              data-tour-id="copy-evaluation"
              onClick={handleCopyEvaluation}
            />
          </Tooltip>
        </Flex>
      </Flex>
      <Divider my={0} flexShrink={0} />
      <Flex
        direction="column"
        gap={6}
        flex="1"
        minH="0"
        overflowY="auto"
        mt={6}
      >
        <SectionHeading>Summary</SectionHeading>
        <Box fontSize="sm" w="100%">
          {aiInterviewEvaluation.summary}
          <Citations
            citations={aiInterviewEvaluation.summaryCitations}
            citationMap={citationMap}
            onClickTimestamp={onPlayMedia}
          />
        </Box>
        {showScores && (
          <Flex flexDir="row" width="100%" mb={6}>
            <Flex flexDir="column" mx={6}>
              <CircularProgress
                value={finalScore}
                color={finalScoreColor}
                trackColor="gray.100"
                size="96px"
                capIsRound
                thickness="6px"
              >
                <CircularProgressLabel color={finalScoreColor}>
                  <Text color={finalScoreColor} fontSize="4xl">
                    {finalScore}
                  </Text>
                </CircularProgressLabel>
              </CircularProgress>
              <EvaluationScorePopover evaluation={aiInterviewEvaluation} />
            </Flex>
            <Flex flexDir="column">
              {(aiInterviewEvaluation.keyPoints || []).map((keyPoint) => (
                <Flex
                  flexDir="row"
                  key={keyPoint.id}
                  alignItems="flex-start"
                  mb={2}
                >
                  {keyPoint.positive ? (
                    <Icon
                      as={HiOutlinePlusCircle}
                      boxSize="5"
                      color="green.400"
                    />
                  ) : (
                    <Icon
                      as={HiOutlineMinusCircle}
                      boxSize="5"
                      color="red.300"
                    />
                  )}
                  <Box fontSize="sm" display="inline" ml="6px">
                    {keyPoint.content}
                    <Citations
                      citations={keyPoint.citations}
                      citationMap={citationMap}
                      onClickTimestamp={onPlayMedia}
                    />
                  </Box>
                </Flex>
              ))}
            </Flex>
          </Flex>
        )}
        <Divider my={0} />

        {/* Questions */}
        <SectionHeading>Questions & Scores</SectionHeading>
        {questions.length ? (
          <VStack spacing={6} w="100%">
            {questions.map((questionEval, index) => (
              <Box key={questionEval.id} w="100%">
                <Flex flexDir="row" alignItems="start" gap="4" mb="4">
                  {showScores && (
                    <ScoreIcon
                      score={questionEval.points || 0}
                      qualifies={questionEval.qualifies}
                      customWeight={questionEval.question.customWeight}
                      redacted={!!questionEval.wasAnswerRedacted}
                      scoringQuestionsCount={
                        (aiInterviewEvaluation.questions || []).filter(
                          (q) =>
                            getQuestionType(q) === AiInterviewQuestionType.Score
                        ).length || 0
                      }
                    />
                  )}
                  <Flex direction="column" gap="2">
                    <Text
                      fontSize="md"
                      fontWeight="semibold"
                      color="gray.800"
                      mt="-1"
                    >
                      {questionEval.question?.question}
                    </Text>
                    {questionEval.wasAnswerRedacted && (
                      <RedactionAlert
                        mb={0}
                        description={getQuestionRedactionDescription(
                          questionEval
                        )}
                      />
                    )}
                    <Box fontSize="sm" fontWeight="normal" color="gray.800">
                      {questionEval.summary}
                      <Citations
                        citations={questionEval.summaryCitations}
                        citationMap={citationMap}
                        onClickTimestamp={onPlayMedia}
                      />
                    </Box>
                  </Flex>
                </Flex>

                {index < questions.length - 1 && <Divider my={0} mt={6} />}
              </Box>
            ))}
          </VStack>
        ) : (
          <Text fontSize="sm" color="gray.500">
            No evaluation available.
          </Text>
        )}

        {(aiInterviewEvaluation.candidateQuestions ?? []).length > 0 && (
          <>
            <Divider my={0} />
            <SectionHeading>Candidate Questions</SectionHeading>
            <CandidateQuestionsSection
              candidateQuestions={
                aiInterviewEvaluation.candidateQuestions ?? []
              }
              citationMap={citationMap}
              onPlayMedia={onPlayMedia}
            />
          </>
        )}
      </Flex>
    </Flex>
  );
};

const SectionHeading: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <Heading
    w="100%"
    fontSize="xs"
    fontWeight="600"
    color="gray.500"
    textTransform="uppercase"
  >
    {children}
  </Heading>
);

export default Evaluation;
